<template>
  <div class="picker-page">
    <div id="map" class="picker-map"></div>

    <div class="panel topbar">
      <div class="searchrow">
        <input
          v-model="keyword"
          type="search"
          placeholder="搜索地点（如：西湖、XX大厦）"
          enterkeyhint="search"
          @keydown.enter.prevent="doSearch"
        />
        <button @click="doSearch">搜索</button>
      </div>
      <div class="coordbar">
        <div class="coord">{{ coordText }}</div>
        <button @click="locateMe">定位</button>
      </div>
      <div class="results" :class="{ show: resultsVisible }">
        <template v-if="pois.length">
          <div v-for="(poi, index) in pois" :key="index" class="result-item" @click="pickPoi(poi)">
            <div class="result-name">{{ poi.name }}</div>
            <div v-if="poi.address" class="result-addr">{{ poi.address }}</div>
          </div>
        </template>
        <div v-else class="result-empty">{{ resultsTip }}</div>
      </div>
    </div>

    <div class="panel bottombar">
      <div class="addr">{{ addrText }}</div>
      <div class="actions">
        <button @click="onCancel">取消</button>
        <button class="primary" @click="onConfirm">确认选点</button>
      </div>
    </div>

    <div v-if="maskVisible" class="mask">
      <div class="mask-box" v-html="maskText"></div>
    </div>
  </div>
</template>

<script>
import wx from 'weixin-js-sdk'

// 天地图 Key（TK）申请地址：https://console.tianditu.gov.cn/api/key
// 支持通过地址参数覆盖：/#/picker?tk=xxx&lng=xxx&lat=xxx
const DEFAULT_TK = 'f529065dee0f2146d458135b14477172'
const API_HOST = 'https://api.tianditu.gov.cn'
const DEFAULT_LNG = 116.3975
const DEFAULT_LAT = 39.9087

export default {
  name: 'Picker',
  data() {
    return {
      tk: '',
      keyword: '',
      coordText: '正在初始化…',
      addrText: '拖动地图或点击地图选择位置',
      resultsVisible: false,
      resultsTip: '',
      pois: [],
      maskVisible: true,
      maskText: '正在加载天地图…',
      geocodeSeq: 0,
      searchTimer: null,
      picked: { lng: DEFAULT_LNG, lat: DEFAULT_LAT, address: '' }
    }
  },
  mounted() {
    const query = this.$route.query
    this.tk = query.tk || DEFAULT_TK
    const lng = parseFloat(query.lng)
    const lat = parseFloat(query.lat)
    this.picked.lng = isFinite(lng) ? lng : DEFAULT_LNG
    this.picked.lat = isFinite(lat) ? lat : DEFAULT_LAT
    this.map = null
    this.marker = null

    document.body.style.overflow = 'hidden'
    document.title = '选择位置'
    this.boot()
  },
  beforeUnmount() {
    if (this.searchTimer) clearTimeout(this.searchTimer)
    document.body.style.overflow = ''
  },
  watch: {
    // 输入即搜索（防抖 400ms），无需手动点「搜索」
    keyword(val) {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      if (!val || !val.trim()) {
        this.hideResults()
        return
      }
      this.searchTimer = setTimeout(() => {
        this.doSearch()
      }, 400)
    }
  },
  methods: {
    showMask(html) {
      this.maskText = html
      this.maskVisible = true
    },
    hideMask() {
      this.maskVisible = false
    },
    // 只有在小程序 web-view 中才回传数据，普通浏览器里用于调试
    getMiniProgram() {
      if (!/miniProgram/i.test(navigator.userAgent)) return null
      if (wx && wx.miniProgram) return wx.miniProgram
      return window.wx && window.wx.miniProgram ? window.wx.miniProgram : null
    },
    loadScript(src) {
      return new Promise((resolve, reject) => {
        const s = document.createElement('script')
        s.src = src
        s.onload = resolve
        s.onerror = () => reject(new Error('加载失败: ' + src))
        document.head.appendChild(s)
      })
    },
    boot() {
      if (!this.tk || this.tk.indexOf('填写') > -1) {
        this.showMask(
          '请先填写天地图 Key（TK）：<br/>把 <b>src/views/picker/index.vue</b> 中的 DEFAULT_TK 改成你申请的 Key。<br/>' +
            '申请地址：<a href="https://console.tianditu.gov.cn/api/key" target="_blank">console.tianditu.gov.cn/api/key</a>'
        )
        return
      }
      this.showMask('正在加载天地图…')
      this.loadScript(API_HOST + '/api?v=4.0&tk=' + encodeURIComponent(this.tk))
        .then(() => {
          if (!window.T) throw new Error('天地图 API 未挂载')
          this.initMap()
        })
        .catch(() => {
          this.showMask(
            '天地图 API 加载失败，请检查：<br/>1）网络是否正常；<br/>2）Key 是否正确；<br/>' +
              '3）该 Key 的授权域名是否包含当前域名。'
          )
        })
    },
    initMap() {
      const T = window.T
      this.map = new T.Map('map')
      this.map.centerAndZoom(new T.LngLat(this.picked.lng, this.picked.lat), 16)
      this.map.enableScrollWheelZoom()
      // 点击地图选点
      this.map.addEventListener('click', (e) => {
        this.hideResults()
        this.setPoint(e.lnglat.getLng(), e.lnglat.getLat())
      })
      this.setPoint(this.picked.lng, this.picked.lat, true)
      this.hideMask()
    },
    setPoint(lng, lat, keepView, knownAddress) {
      const T = window.T
      this.picked.lng = lng
      this.picked.lat = lat
      this.picked.address = knownAddress || ''
      this.coordText = '经度 ' + lng.toFixed(6) + ' / 纬度 ' + lat.toFixed(6)

      if (this.marker && this.map) {
        this.map.removeOverLay(this.marker)
      }
      this.marker = new T.Marker(new T.LngLat(lng, lat))
      this.map.addOverLay(this.marker)
      if (!keepView) {
        this.map.panTo(new T.LngLat(lng, lat))
      }

      if (knownAddress) {
        // 搜索结果自带地址，不再请求逆地理编码
        this.addrText = knownAddress
      } else {
        this.addrText = '正在获取地址…'
        this.reverseGeocode(lng, lat)
      }
    },
    // 逆地理编码（天地图 REST 接口）：仅用于展示地址，失败不影响选点
    reverseGeocode(lng, lat) {
      const seq = ++this.geocodeSeq
      const url =
        API_HOST +
        '/geocoder?postStr=' +
        encodeURIComponent(JSON.stringify({ lon: lng, lat: lat, ver: 1 })) +
        '&type=geocode&tk=' +
        encodeURIComponent(this.tk)

      fetch(url)
        .then((r) => r.json())
        .then((res) => {
          if (seq !== this.geocodeSeq) return // 已有更新的选点，忽略旧结果
          if (res && res.code && res.code !== 0) {
            this.addrText = '地址获取失败：' + (res.msg || res.code)
            return
          }
          const result = res && res.result
          const addr = (result && (result.formatted_address || result.address)) || ''
          if (addr) {
            this.picked.address = addr
            this.addrText = addr
          } else {
            this.addrText = '未获取到地址，可直接使用坐标'
          }
        })
        .catch(() => {
          if (seq !== this.geocodeSeq) return
          this.addrText = '地址获取失败（可能被跨域限制），不影响选点'
        })
    },
    getMapBound() {
      try {
        const b = this.map.getBounds()
        const sw = b.getSouthWest()
        const ne = b.getNorthEast()
        return sw.getLng() + ',' + sw.getLat() + ',' + ne.getLng() + ',' + ne.getLat()
      } catch (e) {
        const d = 0.05
        return this.picked.lng - d + ',' + (this.picked.lat - d) + ',' + (this.picked.lng + d) + ',' + (this.picked.lat + d)
      }
    },
    // 地点搜索：天地图「地名搜索 2.0」REST 接口（mapBound 必填）
    fetchPoi(keyword) {
      const post = {
        keyWord: keyword,
        mapBound: this.getMapBound(),
        level: 12,
        queryType: 1, // 1 = 普通搜索
        start: 0,
        count: 10
      }

      const url =
        API_HOST +
        '/v2/search?postStr=' +
        encodeURIComponent(JSON.stringify(post)) +
        '&type=query&tk=' +
        encodeURIComponent(this.tk)

      return fetch(url)
        .then((r) => r.json())
        .then((res) => {
          // 接口出错有两种返回格式：
          // 1) {"code":301012,"msg":"权限类型错误",...}
          // 2) {"count":0,"status":{"infocode":2003,"cndesc":"缺少参数：mapBound"}}
          if (res && res.code && res.code !== 0) {
            throw new Error('天地图接口返回 ' + res.code + '：' + (res.msg || ''))
          }
          const result = res && res.result
          let pois = (res && (res.pois || res.poi)) || (result && (result.poi || result.pois)) || []
          const infocode = res && res.status && res.status.infocode
          if (!pois.length && infocode && infocode !== 1000) {
            throw new Error('天地图接口返回 ' + infocode + '：' + ((res.status && res.status.cndesc) || ''))
          }
          // 搜索行政区（如市、区县名）时结果可能只返回 area
          const area = result && result.area
          if (area && area.lonlat) {
            pois = [{ name: area.name, address: area.address || '行政区', lonlat: area.lonlat }].concat(pois)
          }
          return this.normalizePois(pois)
        })
    },
    normalizePois(pois) {
      const list = []
      pois.forEach((poi) => {
        const lonlat = poi.lonlat || (poi.location ? poi.location.lon + ',' + poi.location.lat : '')
        const parts = String(lonlat).split(',')
        const lng = parseFloat(parts[0])
        const lat = parseFloat(parts[1])
        if (!isFinite(lng) || !isFinite(lat)) return
        list.push({
          name: poi.name || '未命名地点',
          address: poi.address || poi.poiType || '',
          lng: lng,
          lat: lat
        })
      })
      return list
    },
    doSearch() {
      // 立即搜索（点按钮/回车）时，取消仍在等待的防抖任务，避免重复请求
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
        this.searchTimer = null
      }
      if (!this.map) return
      const keyword = (this.keyword || '').trim()
      if (!keyword) {
        this.showResults([], '请输入要搜索的地点')
        return
      }
      this.showResults([], '搜索中…')

      this.fetchPoi(keyword)
        .then((pois) => this.showResults(pois, '没有找到相关地点，换个关键词试试'))
        .catch((err) => {
          this.showResults([], '搜索失败：' + (err && err.message ? err.message : '网络或跨域限制'))
        })
    },
    showResults(pois, tip) {
      this.pois = pois || []
      this.resultsTip = tip || ''
      this.resultsVisible = true
    },
    hideResults() {
      this.resultsVisible = false
    },
    pickPoi(poi) {
      const T = window.T
      this.hideResults()
      this.setPoint(poi.lng, poi.lat, true, poi.address || poi.name)
      this.map.centerAndZoom(new T.LngLat(poi.lng, poi.lat), 16)
    },
    // 浏览器定位：微信内 H5 的 navigator.geolocation 稳定性一般，仅作为可选项
    locateMe() {
      if (!navigator.geolocation) {
        this.addrText = '当前环境不支持浏览器定位，请手动点击地图选点'
        return
      }
      this.addrText = '正在定位…'
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          this.setPoint(pos.coords.longitude, pos.coords.latitude)
        },
        () => {
          this.addrText = '浏览器定位失败（微信内 H5 定位不稳定），请手动点击地图选点'
        },
        { enableHighAccuracy: true, timeout: 6000, maximumAge: 0 }
      )
    },
    // 确认选点：postMessage 回传数据 + 返回小程序
    onConfirm() {
      const payload = {
        type: 'picked',
        longitude: Number(this.picked.lng.toFixed(6)),
        latitude: Number(this.picked.lat.toFixed(6)),
        coordinate: 'wgs84', // 天地图底图为 CGCS2000，日常展示与 wgs84 视为一致
        address: this.picked.address || ''
      }

      const mp = this.getMiniProgram()
      if (!mp) {
        // 直接在浏览器里打开时的调试提示
        alert(
          '当前不在小程序 web-view 中（可用于浏览器内验证地图与选点）。\n选点结果：' +
            payload.longitude +
            ', ' +
            payload.latitude
        )
        return
      }

      mp.postMessage({ data: payload }) // 消息会在「小程序后退/组件销毁」时送达小程序
      this.showMask('已选择该位置，正在返回…')
      setTimeout(() => mp.navigateBack(), 200)
    },
    onCancel() {
      const mp = this.getMiniProgram()
      if (mp) mp.navigateBack()
    }
  }
}
</script>

<style scoped>
.picker-page {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow: hidden;
  color: #1f2329;
  font-family: -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Helvetica Neue', Arial, sans-serif;
  -webkit-tap-highlight-color: transparent;
}

/* z-index 让地图容器形成独立层叠上下文，防止天地图内部控件盖住页面浮层 */
.picker-map {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
}

.panel {
  position: fixed;
  left: 12px;
  right: 12px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.96);
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  z-index: 100;
}

.topbar {
  top: 12px;
}

.searchrow {
  display: flex;
  align-items: center;
  gap: 8px;
}

.searchrow input {
  flex: 1;
  min-width: 0;
  -webkit-appearance: none;
  appearance: none;
  padding: 10px 12px;
  border: 1px solid #e3e6ea;
  border-radius: 10px;
  background: #f7f8fa;
  color: #1f2329;
  font-size: 16px;
  outline: none;
}

.searchrow input::placeholder {
  color: #a5abb5;
}

.coordbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.coord {
  flex: 1;
  font-size: 13px;
  line-height: 1.5;
  word-break: break-all;
}

.results {
  display: none;
  max-height: 220px;
  margin-top: 8px;
  overflow-y: auto;
  border-top: 1px solid #eef0f4;
  -webkit-overflow-scrolling: touch;
}

.results.show {
  display: block;
}

.result-item {
  padding: 10px 2px;
  border-bottom: 1px solid #f2f4f7;
}

.result-item:last-child {
  border-bottom: none;
}

.result-name {
  font-size: 14px;
  font-weight: 600;
  color: #1f2329;
}

.result-addr {
  margin-top: 2px;
  font-size: 12px;
  line-height: 1.5;
  color: #8a8f99;
}

.result-empty {
  padding: 10px 2px;
  font-size: 13px;
  color: #8a8f99;
}

.bottombar {
  bottom: 12px;
}

.addr {
  min-height: 20px;
  margin-bottom: 8px;
  color: #4b5058;
  font-size: 13px;
  line-height: 1.5;
  word-break: break-all;
}

.actions {
  display: flex;
  gap: 8px;
}

.picker-page button {
  border: none;
  border-radius: 10px;
  padding: 10px 14px;
  background: #eef0f4;
  color: #1f2329;
  font-size: 15px;
  white-space: nowrap;
}

.picker-page button.primary {
  flex: 1;
  background: #07c160;
  color: #ffffff;
  font-weight: 600;
}

.mask {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.45);
  z-index: 200;
}

.mask-box {
  max-width: 320px;
  padding: 16px 18px;
  background: #ffffff;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.7;
}

.mask-box a {
  color: #07c160;
}
</style>
